import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Gear, Lock, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PILAR_COLORS = {
  1: "border-blue-200 bg-blue-50",
  2: "border-orange-200 bg-orange-50",
  3: "border-emerald-200 bg-emerald-50",
  4: "border-purple-200 bg-purple-50",
  5: "border-amber-200 bg-amber-50",
  6: "border-rose-200 bg-rose-50",
  7: "border-cyan-200 bg-cyan-50",
};

export default function ModuleCenter() {
  const { checkAuth } = useAuth();
  const { t } = useLanguage();
  const [modules, setModules] = useState([]);
  const [businessType, setBusinessType] = useState("autonomo");
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  useEffect(() => { fetchModules(); }, []);

  const fetchModules = async () => {
    try {
      const res = await axios.get(`${API}/modules`);
      setModules(res.data.modules);
      setBusinessType(res.data.business_type);
    } catch (error) {
      toast.error("Erro ao carregar módulos");
    } finally {
      setLoading(false);
    }
  };

  const performToggle = async (moduleId, enable) => {
    setToggling(moduleId);
    try {
      await axios.put(`${API}/modules/toggle`, { module_id: moduleId, enabled: enable });
      setModules((prev) => prev.map((m) => m.id === moduleId ? { ...m, enabled: enable } : m));
      await checkAuth();
      toast.success(enable ? "Módulo ativado!" : "Módulo desativado.");
    } catch (error) {
      toast.error("Erro ao alterar módulo");
    } finally {
      setToggling(null);
    }
  };

  const handleToggle = (moduleId, currentEnabled) => {
    if (currentEnabled) {
      performToggle(moduleId, false);
      return;
    }
    // Enabling: just toggle directly
    performToggle(moduleId, true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" />
      </div>
    );
  }

  const coreModules = [
    "Fichas Técnicas", "Precificação", "Estoque Básico",
    "Sous-Chef IA", "Eventos e Produção", "Lista de Compras",
    "Plano de Ataque", "Medidas", "Análises",
  ];

  return (
    <div className="space-y-6" data-testid="module-center-page">
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
          <Gear size={24} weight="bold" />
          {t("mod_title")}
        </h2>
        <p className="text-sm text-zinc-500 mt-1">
          Ative ou desative funcionalidades do seu painel.
          Perfil: <span className="font-semibold text-zinc-700 capitalize">{businessType}</span>
        </p>
      </div>

      {/* Core */}
      <Card className="border-zinc-200 shadow-sm">
        <CardContent className="py-5 px-5">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={16} className="text-zinc-400" />
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Core Operacional — Sempre Ativo</Label>
          </div>
          <div className="flex flex-wrap gap-2">
            {coreModules.map((name) => (
              <span key={name} className="text-xs bg-zinc-100 text-zinc-600 border border-zinc-200 px-2.5 py-1 rounded font-medium">{name}</span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Advanced */}
      <div className="space-y-3">
        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Módulos Avançados de Escala</Label>
        {modules.map((mod) => (
          <Card
            key={mod.id}
            data-testid={`module-card-${mod.id}`}
            className={`border shadow-sm transition-all ${mod.enabled ? PILAR_COLORS[mod.pilar] || "border-zinc-200 bg-zinc-50" : "border-zinc-200"}`}
          >
            <CardContent className="py-4 px-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded">P{mod.pilar}</span>
                    <p className="text-sm font-bold text-zinc-900">{mod.name}</p>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{mod.description}</p>
                </div>
                <Switch
                  checked={mod.enabled}
                  disabled={toggling === mod.id}
                  onCheckedChange={() => handleToggle(mod.id, mod.enabled)}
                  data-testid={`module-toggle-${mod.id}`}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-lg">
        <p className="text-xs text-zinc-500 flex items-center gap-1.5">
          <Info size={14} />
          Desativar um módulo remove as telas do menu, mas <strong>preserva todos os seus dados</strong>.
        </p>
      </div>
    </div>
  );
}
